import { useCallback, useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const Stars = ({ value, onChange, size = "w-4 h-4" }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid={onChange ? `review-star-${n}` : undefined}
        disabled={!onChange}
        onClick={() => onChange && onChange(n)}
        className={onChange ? "hover:scale-110 transition-transform" : "cursor-default"}
      >
        <Star className={`${size} ${n <= value ? "fill-[#7DC242] text-[#7DC242]" : "text-[#E7E0D8]"}`} />
      </button>
    ))}
  </span>
);

export const Reviews = ({ slug }) => {
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });
  const [form, setForm] = useState({ name: "", rating: 0, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => api.get(`/products/${slug}/reviews`).then((r) => setData(r.data)).catch(() => {}), [slug]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.rating === 0) return toast.error("Seleziona un voto in stelle");
    setSubmitting(true);
    try {
      await api.post(`/products/${slug}/reviews`, form);
      toast.success("Grazie per la tua recensione!");
      setForm({ name: "", rating: 0, comment: "" });
      load();
    } catch {
      toast.error("Errore nell'invio della recensione");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="reviews-section" className="mt-16 lg:mt-20 max-w-3xl">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <h2 className="font-serif-display font-bold text-2xl sm:text-3xl text-[#1C1917]">Recensioni</h2>
        {data.count > 0 && (
          <div className="flex items-center gap-2" data-testid="reviews-average">
            <Stars value={Math.round(data.average)} />
            <span className="font-mono-accent text-sm text-[#44403C]">{data.average} · {data.count} recension{data.count === 1 ? "e" : "i"}</span>
          </div>
        )}
      </div>

      <form onSubmit={submit} className="rounded-2xl border border-[#E7E0D8] bg-white p-6 mb-8">
        <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#2E8A57] mb-4">Lascia una recensione</p>
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center mb-4">
          <input
            data-testid="review-name-input"
            required
            maxLength={60}
            placeholder="Il tuo nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="flex-1 rounded-xl border border-[#E7E0D8] px-4 py-3 text-sm focus:outline-none focus:border-[#2E8A57]"
          />
          <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size="w-6 h-6" />
        </div>
        <textarea
          data-testid="review-comment-input"
          rows={3}
          maxLength={1000}
          placeholder="Com'era il piatto? (facoltativo)"
          value={form.comment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
          className="w-full rounded-xl border border-[#E7E0D8] px-4 py-3 text-sm focus:outline-none focus:border-[#2E8A57]"
        />
        <button data-testid="review-submit-btn" type="submit" disabled={submitting} className="btn-tuscan mt-4 px-8 py-3.5 rounded-full text-sm font-semibold disabled:opacity-40">
          {submitting ? "Invio..." : "Invia recensione"}
        </button>
      </form>

      <div className="space-y-5">
        {data.reviews.length === 0 && (
          <p data-testid="no-reviews-message" className="text-sm text-[#78716C]">Nessuna recensione ancora — sii il primo a raccontare com'era!</p>
        )}
        {data.reviews.map((r) => (
          <div key={r.id} data-testid={`review-${r.id}`} className="border-b border-[#E7E0D8] pb-5">
            <div className="flex items-center gap-3">
              <span className="font-serif-display font-semibold text-[#1C1917]">{r.name}</span>
              <Stars value={r.rating} />
              <span className="font-mono-accent text-[10px] text-[#78716C] ml-auto">{new Date(r.created_at).toLocaleDateString("it-IT")}</span>
            </div>
            {r.comment && <p className="mt-2 text-sm text-[#44403C] leading-relaxed">{r.comment}</p>}
          </div>
        ))}
      </div>
    </section>
  );
};
